import { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { getMediaList, deleteMedia, uploadImage } from '@/lib/api';
import { getImageUrl } from '@/components/ImageUploadField';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Trash2, Copy, Check, Upload, Images, Loader2, RefreshCw, X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function MediaCard({ image, onDelete, onCopy, copied }) {
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const imageUrl = `${BACKEND_URL}/api/images/${image.id}`;
  const relativeUrl = `/api/images/${image.id}`;

  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      setTimeout(() => setConfirmDelete(false), 3000);
      return;
    }
    setDeleting(true);
    await onDelete(image.id);
    setDeleting(false);
  };

  return (
    <div
      className="group relative bg-card border border-border rounded-xl overflow-hidden flex flex-col transition-shadow hover:shadow-lg"
      data-testid={`media-card-${image.id}`}
    >
      {/* Thumbnail */}
      <div className="relative aspect-square bg-muted overflow-hidden">
        <img
          src={imageUrl}
          alt={image.filename}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          loading="lazy"
        />
        {/* Overlay actions on hover */}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          <button
            onClick={() => onCopy(relativeUrl)}
            className="p-2 rounded-full bg-white/90 hover:bg-white text-zinc-800 transition-colors"
            title="Copier l'URL"
            data-testid={`media-copy-${image.id}`}
          >
            {copied === relativeUrl ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className={`p-2 rounded-full transition-colors ${
              confirmDelete
                ? 'bg-red-500 text-white hover:bg-red-600'
                : 'bg-white/90 hover:bg-white text-zinc-800'
            }`}
            title={confirmDelete ? 'Cliquer pour confirmer la suppression' : 'Supprimer'}
            data-testid={`media-delete-${image.id}`}
          >
            {deleting
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : confirmDelete
                ? <X className="w-4 h-4" />
                : <Trash2 className="w-4 h-4" />
            }
          </button>
        </div>
        {/* Confirm badge */}
        {confirmDelete && (
          <div className="absolute top-2 left-0 right-0 flex justify-center">
            <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium">
              Cliquer pour confirmer
            </span>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-3 flex flex-col gap-1 flex-1">
        <p
          className="text-sm font-medium text-foreground truncate"
          title={image.filename}
        >
          {image.filename}
        </p>
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{formatDate(image.created_at)}</span>
          <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded">
            {image.content_type?.split('/')[1]?.toUpperCase() || 'IMG'}
          </span>
        </div>
      </div>
    </div>
  );
}

export default function AdminMedia() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const [hasError, setHasError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setHasError(false);
    try {
      const res = await getMediaList();
      setImages(res.data || []);
    } catch (err) {
      const status = err.response?.status;
      if (status !== 401 && status !== 403) {
        const detail = status ? ` (erreur ${status})` : ' (serveur inaccessible)';
        toast.error(`Erreur lors du chargement de la médiathèque${detail}`);
        setHasError(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (localStorage.getItem('tapisstore_admin_token')) {
      load();
    } else {
      setLoading(false);
    }
  }, [load]);

  const handleDelete = async (imageId) => {
    try {
      await deleteMedia(imageId);
      setImages(prev => prev.filter(img => img.id !== imageId));
      toast.success('Image supprimée');
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleCopy = (url) => {
    navigator.clipboard.writeText(url);
    setCopied(url);
    toast.success('URL copiée dans le presse-papiers');
    setTimeout(() => setCopied(null), 2000);
  };

  const handleUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    let successCount = 0;
    for (const file of Array.from(files)) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        await uploadImage(formData);
        successCount++;
      } catch {
        toast.error(`Erreur pour ${file.name}`);
      }
    }
    if (successCount > 0) {
      toast.success(`${successCount} image${successCount > 1 ? 's' : ''} téléchargée${successCount > 1 ? 's' : ''}`);
      await load();
    }
    setUploading(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleUpload(e.dataTransfer.files);
  };

  const handleFileInput = (e) => handleUpload(e.target.files);

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-media-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2">
              <Images className="w-6 h-6 text-primary" />
              Médiathèque
            </h1>
            <p className="text-muted-foreground mt-1">
              {images.length} image{images.length !== 1 ? 's' : ''} stockée{images.length !== 1 ? 's' : ''} dans MongoDB
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              data-testid="media-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
            <label htmlFor="media-upload-input">
              <Button
                asChild
                size="sm"
                disabled={uploading}
                data-testid="media-upload-btn"
              >
                <span>
                  {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                  Ajouter des images
                </span>
              </Button>
            </label>
            <input
              id="media-upload-input"
              type="file"
              multiple
              accept="image/jpeg,image/png,image/gif,image/webp"
              className="hidden"
              onChange={handleFileInput}
            />
          </div>
        </div>

        {/* Drop Zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
            dragOver
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-primary/50 hover:bg-muted/30'
          }`}
          onClick={() => document.getElementById('media-upload-input').click()}
          data-testid="media-dropzone"
        >
          <Upload className={`w-8 h-8 mx-auto mb-2 ${dragOver ? 'text-primary' : 'text-muted-foreground'}`} />
          <p className="text-sm font-medium text-foreground">
            Glissez-déposez vos images ici
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            JPEG, PNG, GIF, WebP · Max 10 Mo par image
          </p>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="media-loading">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : hasError ? (
          <div className="text-center py-20 text-muted-foreground" data-testid="media-error">
            <Images className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p className="font-medium text-destructive">Impossible de charger la médiathèque</p>
            <p className="text-sm mt-1 mb-4">Le serveur est peut-être temporairement indisponible.</p>
            <Button variant="outline" size="sm" onClick={load}>
              <RefreshCw className="w-4 h-4 mr-2" /> Réessayer
            </Button>
          </div>
        ) : images.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground" data-testid="media-empty">
            <Images className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p className="font-medium">Aucune image dans la médiathèque</p>
            <p className="text-sm mt-1">Uploadez vos premières images ci-dessus</p>
          </div>
        ) : (
          <div
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4"
            data-testid="media-grid"
          >
            {images.map((image) => (
              <MediaCard
                key={image.id}
                image={image}
                onDelete={handleDelete}
                onCopy={handleCopy}
                copied={copied}
              />
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
